import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import { CanvasAlignment } from '@/components/canvas/constants'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useDualCanvasSize } from '@/hooks/useDualCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { useAppSelector } from '@/store'
import {
  useGenerateRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  ImageCalibrationStatus,
  RecipeGenerationStatus,
  setImageCalibrationStatus,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import {
  setSelectedStumpShadeKey,
  setStumpShadeRecipe,
} from '@/store/stainingStudio'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  getProtocolValue,
  hasNonEmptyOutputDualCanvas,
  isImageCalibrated,
  saveRecipeGeneric,
} from '@/utils/helper'
import { buildStainingColorModelPayload } from '@/utils/matisseV2/stainingColorModel'

import StumpShadePanel from './StumpShadePanel'

const togglePlacement = (
  currentPlacement: string,
): PLACEMENT.LEFT | PLACEMENT.RIGHT =>
  currentPlacement === PLACEMENT.LEFT ? PLACEMENT.RIGHT : PLACEMENT.LEFT

function StumpShade() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alignment = CanvasAlignment.VERTICAL
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)

  const [generateRecipe] = useGenerateRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipMessage, setTooltipMessage] = useState('')
  const [chipHeight, setChipHeight] = useState(0)
  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [circleColor, setCircleColor] = useState<string>('#ffffff')
  const [activeCanvas, setActiveCanvas] = useState<PLACEMENT | null>(null)

  const isBothSideImageSelected =
    !!dualCanvasImage?.[PLACEMENT.LEFT]?.id &&
    !!dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const selectedStumpShadeStainingBrand = useAppSelector(
    (state) => state.stainingStudio.selectedStumpShadeBrand,
  )

  const selectedStumpShadeKey = useAppSelector(
    (state) => state.stainingStudio.selectedStumpShadeKey,
  )
  const stumpShadeRecipe = useAppSelector(
    (state) => state.stainingStudio.stumpShadeRecipe,
  )

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const selectedColor = useAppSelector((store) => store.app.selectedColor)

  const chipsContainerRef = useRef<HTMLDivElement | null>(null)

  const hasStumpShadeData =
    Object.keys(stumpShadeRecipe?.left || {}).length > 0 ||
    Object.keys(stumpShadeRecipe?.right || {}).length > 0

  useEffect(() => {
    if (chipsContainerRef.current && hasStumpShadeData) {
      setChipHeight(chipsContainerRef.current.clientHeight)
    } else {
      setChipHeight(0)
    }
  }, [hasStumpShadeData, stumpShadeRecipe])

  const { containerRef, dimensions } = useDualCanvasSize(alignment, chipHeight)

  const isLeftImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.LEFT],
  )

  const isRightImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.RIGHT],
  )

  const isBothImageCalibrated = isLeftImageCalibrated && isRightImageCalibrated

  const placement = useAppSelector((state) => state.app.placement)
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const calibrateImageIfNeeded = (
    type: string,
    currentPlacement: PLACEMENT,
  ) => {
    const imageId = dualCanvasImage?.[currentPlacement]?.id

    if (
      !imageId ||
      type !== imageType.RAW ||
      imageCalibrationStatus?.[imageId] !== ImageCalibrationStatus.CALIBRATING
    ) {
      return
    }

    dispatch(
      setImageCalibrationStatus({
        id: imageId,
        status: ImageCalibrationStatus.CALIBRATED,
      }),
    )
  }

  const hasTargetColor = (placementToCheck: PLACEMENT, key: string) => {
    const recipeByPlacement = (stumpShadeRecipe as any)?.[
      placementToCheck as any
    ]

    return !isEmpty(recipeByPlacement?.[key]?.input?.targetcolor)
  }

  const startFirstStep = (
    type: string,
    currentPlacement: PLACEMENT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStumpShadeKey) return

    const toggledPlacement = togglePlacement(currentPlacement)

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATE))

    const payload = {
      basecolor: selectedStumpShadeKey,
      protocol: getProtocolValue(dualCanvasImage?.[currentPlacement]),
      stainingbrand: selectedStumpShadeStainingBrand || '',
      targetcolor: [
        selectedColor.L,
        selectedColor.a,
        selectedColor.b,
      ] as string[],
      crownPlacement: toggledPlacement,
    }

    setTooltipOpen(true)
    setTooltipMessage(t('stainingCorrection.panel.secondStep'))

    dispatch(
      setStumpShadeRecipe({
        placement: currentPlacement,
        key: selectedStumpShadeKey,
        data: {
          input: {
            ...payload,
            x: elementPos?.x,
            y: elementPos?.y,
            backgroundcolor: [],
          } as any,
          output: {},
        },
      }),
    )
  }

  const startSecondStep = (
    type: string,
    currentPlacement: PLACEMENT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStumpShadeKey) return

    const imageId = dualCanvasImage?.[currentPlacement]?.id
    const toggledPlacement = togglePlacement(currentPlacement)

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATING))

    const payload = buildStainingColorModelPayload({
      backgroundcolor: [selectedColor.L, selectedColor.a, selectedColor.b],
      aestheticmodelstainingbrand: selectedStumpShadeStainingBrand || '',
      protocol: getProtocolValue(dualCanvasImage?.[currentPlacement]),
      targetcolor: (stumpShadeRecipe as any)?.[toggledPlacement as any]?.[
        selectedStumpShadeKey
      ]?.input?.targetcolor,
    })

    toast.info(t('recipeGenerate.inProgress'))

    generateRecipe({
      caseId,
      recipePath: 'staining-color-model',
      data: payload,
    })
      .unwrap()
      .then((res) => {
        const { generation_id, ...output } = res ?? {}
        setTooltipOpen(false)
        dispatch(
          setStumpShadeRecipe({
            placement: currentPlacement,
            key: selectedStumpShadeKey,
            data: {
              input: {
                ...{
                  ...payload,
                  targetcolor: [],
                  basecolor: selectedStumpShadeKey,
                },
                x: elementPos?.x,
                y: elementPos?.y,
              } as any,
              output,
              generation_id,
            },
          }),
        )
        toast.success(t('stainingCorrection.panel.recipeGenerated'))
        dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATED))
        dispatch(setSelectedStumpShadeKey(null))
        setTooltipOpen(false)
      })
      .catch((error) => {
        toast.error(
          error?.data?.message ||
            t('stainingCorrection.panel.recipeGenerationFailed'),
        )
      })
  }

  const handleRecipeGeneration = (
    type: string,
    currentPlacement: PLACEMENT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStumpShadeKey) {
      return
    }

    const toggledPlacement = togglePlacement(currentPlacement)

    const hasCurrentTarget = hasTargetColor(
      currentPlacement,
      selectedStumpShadeKey,
    )
    const hasToggledTarget = hasTargetColor(
      toggledPlacement,
      selectedStumpShadeKey,
    )

    if (!hasCurrentTarget && !hasToggledTarget) {
      startFirstStep(type, currentPlacement, elementPos)
      return
    }

    if (!hasCurrentTarget) {
      startSecondStep(type, currentPlacement, elementPos)
    }
  }

  const onImageClick = (
    type: string,
    currentPlacement: PLACEMENT,
    elementPos?: { x: number; y: number },
  ) => {
    const imageId = dualCanvasImage?.[currentPlacement]?.id

    if (!imageId) {
      return
    }

    calibrateImageIfNeeded(type, currentPlacement)

    if (!isBothImageCalibrated) {
      return
    }

    if (!selectedStumpShadeStainingBrand) {
      toast.error(t('stainingCorrection.panel.selectValidStainingBrand'))
      return
    }

    handleRecipeGeneration(type, currentPlacement, elementPos)
  }

  const onSaveAsImage = () => {
    generateImage(combinedRef as any, 'Stump Shade-Recipe.png')
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      dualCanvasImage,
      recipeName,
      output: stumpShadeRecipe,
      workflow: Workflows.STAINING_STUDIO,
      mutationFn: saveRecipe,
      recipeType: 'staining_color_model',
      closeDialog: () => setSaveRecipedialogOpen(false),
      inputType: 'dual',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      dualCanvasImage,
      recipeName,
      output: stumpShadeRecipe,
      workflow: Workflows.STAINING_STUDIO,
      mutationFn: savePdf,
      recipeType: 'staining_color_model',
      inputType: 'dual',
      isPdf: true,
    })
  }

  return (
    <CaseInfoLayout
      disabled={hasNonEmptyOutputDualCanvas(stumpShadeRecipe)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box display="flex" flexGrow={1} ref={containerRef} position="relative">
          {!imageEdit ? (
            <>
              <Box ref={combinedRef} display="flex">
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.LEFT}
                  type={dualCanvasImage?.[PLACEMENT.LEFT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.LEFT]?.id}
                  combinedRef={combinedRef}
                  rawWidth={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_height}
                  isActiveCircle={!!selectedStumpShadeKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.RIGHT}
                  type={dualCanvasImage?.[PLACEMENT.RIGHT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.RIGHT]?.id}
                  combinedRef={combinedRef}
                  rawWidth={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_height}
                  isActiveCircle={!!selectedStumpShadeKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
              </Box>
              {isBothSideImageSelected && isBothImageCalibrated && (
                <StumpShadePanel
                  setTooltipOpen={setTooltipOpen}
                  setTooltipMessage={setTooltipMessage}
                  dimensions={dimensions}
                />
              )}
              {containerRef.current && (
                <SnackbarTooltip
                  containerRef={containerRef}
                  message={tooltipMessage}
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  placement="top"
                  offset={[0, -100]}
                  autoHideDuration={10000}
                  maxWidth={500}
                />
              )}
            </>
          ) : (
            <ImageEdit
              selectedImage={
                placement === PLACEMENT.LEFT
                  ? dualCanvasImage[PLACEMENT.LEFT]
                  : dualCanvasImage[PLACEMENT.RIGHT]
              }
            />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default StumpShade
